import { createSelector } from '@reduxjs/toolkit';
import { NTB_JOURNEY, UK_ONLY_CITIZENSHIP } from 'constants/general';
import moment from 'moment';

/** A valid date is: YYYY-MM-DD */
const validDateRegex = /^\d{4}-\d{2}-\d{2}$/;
/** a partial date looks like: YYYY-MM */
const partialDateRegex = /^\d{4}-\d{2}$/;
const singleMonthPartialDateRegex = /^\d{4}-\d{1}$/;

/**
 * Converts a shortended ISO date YYYY-MM to API required format.
 * @param {string} dateValue
 */
function formatShortenedIsoDate(dateValue) {
  if (dateValue) {
    if (validDateRegex.test(dateValue)) {
      return dateValue;
    }

    if (partialDateRegex.test(dateValue)) {
      return `${dateValue}-01`;
    }

    if (singleMonthPartialDateRegex.test(dateValue)) {
      // Leave this to be formatted correctly by moment instead.
      return dateValue;
    }
  }

  return dateValue;
}

function getDOB(birthDate) {
  if (birthDate) {
    if (validDateRegex.test(birthDate)) {
      return birthDate;
    } else {
      return moment(birthDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    }
  }
  return birthDate;
}

/**
 * Transforms effective period collection to required format.
 * @param {{ from?: string, to?: string }} effectivePeriod
 */
export function transformEffectivePeriod(effectivePeriod) {
  return Object.fromEntries(
    Object.entries(effectivePeriod).map(([key, value]) => [key, formatShortenedIsoDate(value)])
  );
}

export function getAddresses(addressDetails) {
  const addresses = [];

  if (addressDetails.ADDRESS) {
    const currentAddress = addressDetails.ADDRESS;
    if (Object.keys(currentAddress.uk_address).length > 0) {
      const uk_address = getValidUKAddress(currentAddress.uk_address);

      addresses.push({
        uk_address,
        effective_period: {
          from: formatShortenedIsoDate(currentAddress.effective_period.from)
        }
      });
    } else if (
      currentAddress.international_address &&
      Object.keys(currentAddress.international_address).length > 0
    ) {
      addresses.push({
        international_address: currentAddress.international_address,
        effective_period: transformEffectivePeriod(currentAddress.effective_period)
      });
    }
  }

  if (addressDetails.PREVIOUS_ADDRESSES && addressDetails.PREVIOUS_ADDRESSES.length > 0) {
    addresses.push(
      ...addressDetails.PREVIOUS_ADDRESSES.map((previousAddress, index) => {
        let address = {};
        if (previousAddress.uk_address && Object.keys(previousAddress.uk_address).length > 0) {
          const { address_reference, ...uk_address } = getValidUKAddress(
            previousAddress.uk_address
          );
          address = { uk_address };
        } else {
          address = { international_address: previousAddress.international_address };
        }
        return {
          ...address,
          effective_period: {
            from: formatShortenedIsoDate(previousAddress?.effective_period?.from),
            to: formatShortenedIsoDate(
              index === 0
                ? addressDetails.ADDRESS.effective_period?.from
                : previousAddress?.effective_period?.to
                ? previousAddress.effective_period.to
                : addressDetails.PREVIOUS_ADDRESSES[index - 1].effective_period?.from
            )
          }
        };
      })
    );
  }

  return addresses;
}
export function getResumedAddressDetails(address) {
  const resumedAddresses = [];
  const currentAddress = address[0];
  const previousAddresses = [];
  if (Object.keys(currentAddress.uk_address).length > 0) {
    const uk_address = getValidUKAddress(currentAddress.uk_address);
    resumedAddresses.push({
      uk_address,
      effective_period: {
        from: formatShortenedIsoDate(currentAddress.effective_period.from)
      }
    });
  } else if (
    currentAddress.international_address &&
    Object.keys(currentAddress.international_address).length > 0
  ) {
    resumedAddresses.push({
      international_address: currentAddress.international_address,
      effective_period: transformEffectivePeriod(currentAddress.effective_period)
    });
  }
  if (address.length > 1) {
    for (let i = 1; i <= address.length - 1; i++) {
      previousAddresses.push(address[i]);
    }
    let resumedPreviousAddresses = previousAddresses.map((previousAddress) => {
      let address = {};
      if (previousAddress?.uk_address && Object.keys(previousAddress?.uk_address).length > 0) {
        const { address_reference, ...uk_address } = getValidUKAddress(previousAddress?.uk_address);
        address = { uk_address };
      } else {
        address = { international_address: previousAddress?.international_address };
      }
      return {
        ...address,
        effective_period: {
          from: formatShortenedIsoDate(previousAddress?.effective_period?.from),
          to: formatShortenedIsoDate(previousAddress?.effective_period?.to)
        }
      };
    });
    if (resumedPreviousAddresses.length) {
      for (let i = 0; i < resumedPreviousAddresses.length; i++) {
        resumedAddresses.push(resumedPreviousAddresses[i]);
      }
    }
  }
  return resumedAddresses;
}
//convert to valid address as per API schema.so convert street and address_line1 to API require name
function getValidUKAddress(ukAddress) {
  if (!ukAddress) {
    return null;
  }
  return Object.keys(ukAddress).reduce((address, key) => {
    switch (key) {
      case 'street': {
        address.street_name = ukAddress[key];
        break;
      }
      case 'address_line1': {
        address.address_line_1 = ukAddress[key];
        break;
      }
      case 'address_line2': {
        address.address_line_2 = ukAddress[key];
        break;
      }
      /**
       * address_line3 and address_line4 is now invalid for UK Addresses,
       * and will be rejected by the customer-service. Don't add it.
       * However, some existing customers still have this, and the customer-service will add it
       * to the response when retrieving an address.
       * https://github.obps.io/DigitalServicing/customer-services/blob/cb2eb92b6347ae41ff814efae96a9d80d3b5f14c/application/src/main/java/com/thenational/eu/customer/representation/response/getCustomer/builder/GetCustomerResponseBuilder.java#L428
       */
      case 'address_line3':
      case 'address_line4': {
        break;
      }
      case 'house_number': {
        address.house_number = ukAddress[key] || '';
        break;
      }
      default: {
        address[key] = ukAddress[key];
      }
    }

    return address;
  }, {});
}

export function getCustomerState(state) {
  return {
    addresses: getAddresses(state.addressDetails.model),
    residential_status: state.addressDetails.model.residential_status,
    ...state.personalDetails.model,
    ...state.contactDetails.model,
    ...state.employmentDetails.model,
    ...state.financialDetails.model,
    journey: state.config.journey
  };
}

export const customerCreationSelector = createSelector([getCustomerState], (customerState) => {
  return {
    applicants: [
      {
        personal_details: {
          name: {
            title: customerState.name.title,
            first_name: customerState.name.firstName,
            ...(customerState.name.middleName && { middle_name: customerState.name.middleName }),
            last_name: customerState.name.lastName
          },
          sex: customerState.gender,
          dob: customerState.birthDate,
          addresses: customerState.addresses,
          residential_status: customerState.residential_status,
          contact_details: {
            phone: {
              mobile: customerState.mobileNumber,
              ...(customerState.homePhoneNumber && { home: customerState.homePhoneNumber }),
              ...(customerState.workPhoneNumber && { work: customerState.workPhoneNumber })
            },
            email: customerState.emailAddress,
            preferred_contact: customerState.preferredMethod
          }
        }
      }
    ]
  };
});

export const customerDataSelector = createSelector([getCustomerState], (customerState) => {
  let countriesOfCitizenship = [];
  if (!UK_ONLY_CITIZENSHIP.includes(customerState.otherCountry)) {
    countriesOfCitizenship.push(customerState.otherCountry);
    if (customerState.anotherCountry !== 'No') {
      countriesOfCitizenship.push(customerState.anotherCountry);
    }
  }
  return {
    applicants: [
      {
        personal_details: {
          name: {
            title: customerState.name.title,
            first_name: customerState.name.firstName,
            middle_name: customerState.name.middleName,
            last_name: customerState.name.lastName
          },
          sex: customerState.gender,
          dob: getDOB(customerState.birthDate),
          addresses: customerState.addresses,
          residential_status: customerState.residential_status,
          marital_status: customerState.maritalStatus || 'Unknown', //todo: revert to previous change once backend updated
          number_of_dependents: `${customerState.numberOfDependants}` //convert to String as per API mapping
        },
        contact_details: {
          phone: {
            mobile: customerState.mobileNumber,
            ...(customerState.homePhoneNumber && { home: customerState.homePhoneNumber }),
            ...(customerState.workPhoneNumber && { work: customerState.workPhoneNumber })
          },
          email: customerState.emailAddress,
          preferred_contact: customerState.preferredMethod
        },
        ...getEmploymentDetail(customerState),
        ...((customerState.UKCitizenship ||
          customerState.mortgageExpense ||
          customerState.grossAnnualIncome) && {
          financial_details: {
            ...(customerState.UKCitizenship && {
              tax: {
                countries_of_citizenship: countriesOfCitizenship.filter(
                  (country) => country !== ''
                ),
                uk_citizen: customerState.UKCitizenship,
                identification_info: []
              }
            }),
            ...(customerState.mortgageExpense && {
              expenditure: {
                mortgage: {
                  net_amount: customerState.mortgageExpense
                },
                others: {
                  net_amount: customerState.creditExpense
                }
              }
            }),
            ...(customerState.grossAnnualIncome && {
              income: {
                gross_annual: parseInt(customerState.grossAnnualIncome),
                regular: {
                  net_amount: parseInt(customerState.netMonthlyIncome),
                  input_type:
                    customerState.journey === NTB_JOURNEY
                      ? 'HOUSEHOLD INCOME'
                      : 'NET MONTHLY INCOME'
                }
              }
            })
          }
        })
      }
    ]
  };
});

export const getResumedDataSelector = (
  resumedCustomerData,
  employmentDetailsModel,
  addressDetailsModel = {}
) => {
  return {
    applicants: [
      {
        personal_details: {
          name: {
            title: resumedCustomerData.personal_details.name.title,
            first_name: resumedCustomerData.personal_details.name.first_name,
            ...(resumedCustomerData.personal_details.name.middle_name && {
              middle_name: resumedCustomerData.personal_details.name.middle_name
            }),
            last_name: resumedCustomerData.personal_details.name.last_name
          },
          sex: resumedCustomerData.personal_details.gender,
          dob: moment(resumedCustomerData.personal_details.dob).format('YYYY-MM-DD'),
          addresses: Object.keys(addressDetailsModel).length
            ? getAddresses(addressDetailsModel)
            : getResumedAddressDetails(resumedCustomerData.personal_details.addresses),
          residential_status: Object.keys(addressDetailsModel).length
            ? addressDetailsModel.residential_status
            : resumedCustomerData.personal_details.residential_status,
          marital_status: resumedCustomerData.personal_details.marital_status || 'Unknown',
          number_of_dependents: `${
            parseInt(resumedCustomerData.personal_details.number_of_dependents, 10) || '0'
          }`
        },
        contact_details: {
          phone: {
            mobile: resumedCustomerData.contact_details.phone.mobile,
            ...(resumedCustomerData.contact_details.phone.home && {
              home: resumedCustomerData.contact_details.phone.home
            }),
            ...(resumedCustomerData.contact_details.phone.work && {
              work: resumedCustomerData.contact_details.phone.work
            })
          },
          email: resumedCustomerData.contact_details.email,
          preferred_contact: resumedCustomerData.contact_details.preferences.contact.method
        },
        ...getEmploymentDetail(employmentDetailsModel),
        ...getFinancialDetails(
          Object.keys(employmentDetailsModel).length
            ? employmentDetailsModel
            : resumedCustomerData.financial_details
        )
      }
    ]
  };
};

const economicallyInactiveStatus = [
  'UNEMPLOYED',
  'RETIRED',
  'STUDENT',
  'LONG TERM DISABLED',
  'HOUSE PERSON'
];

function getEmploymentDetail(customerState) {
  const { employerName, employmentStatus, employmentStartDate } = customerState;

  /**
   * @note by removing employment status this could
   * remove *all* employment details (e.g employer.name)
   * I have no idea why. Ask Vi-oh wait...
   */
  if (!employmentStatus) {
    return null;
  }

  const isEconomicallyInactive = economicallyInactiveStatus.includes(employmentStatus);

  return {
    employment_details: [
      {
        status: employmentStatus,
        ...(!isEconomicallyInactive && {
          employer: {
            name: employerName
          },
          ...(employmentStartDate && {
            effective_period: {
              from: moment(formatShortenedIsoDate(employmentStartDate), 'YYYY-MM-DD').format(
                'YYYY-MM-DD'
              )
            }
          })
        })
      }
    ]
  };
}

function getFinancialDetails(customerState) {
  let countriesOfCitizenship = [];
  if (!UK_ONLY_CITIZENSHIP.includes(customerState.otherCountry)) {
    countriesOfCitizenship.push(customerState.otherCountry);
    if (customerState.anotherCountry !== 'No') {
      countriesOfCitizenship.push(customerState.anotherCountry);
    }
  }
  return (
    (customerState.UKCitizenship ||
      customerState.mortgageExpense ||
      customerState.grossAnnualIncome) && {
      financial_details: {
        ...(customerState.UKCitizenship && {
          tax: {
            countries_of_citizenship: countriesOfCitizenship.filter((country) => country !== ''),
            uk_citizen: customerState.UKCitizenship,
            identification_info: []
          }
        }),
        ...(customerState.mortgageExpense && {
          expenditure: {
            mortgage: {
              net_amount: customerState.mortgageExpense
            },
            others: {
              net_amount: customerState.creditExpense
            }
          }
        }),
        ...(customerState.grossAnnualIncome && {
          income: {
            gross_annual: parseInt(customerState.grossAnnualIncome),
            regular: {
              net_amount: parseInt(customerState.netMonthlyIncome),
              input_type: 'HOUSEHOLD INCOME'
            }
          }
        })
      }
    }
  );
}
